import * as d3 from 'd3';
import './default.js';
import { frontoAnnotationGenerator } from "./annotation.js";
import { frontoTitleGenerator } from "./title.js";
import { frontoEmphasisGenerator } from "./emphasis.js";
import { frontoBinCalc } from "./bin-calc.js";
import { frontoCompileSpace } from "./space.js";

export function getDatasetInfo(dataset, calculates) {
  let dataInstance = {};
  let data = deepcopy(dataset);
  let calcs = calculates?.map(calc => {
    return { fun: makeFilterFunction(calc.calculate, true), as: calc.as };
  })
  data.forEach(row => {
    calcs?.forEach((calc) => {
      row[calc.as] = calc.fun(row);
    });
    Object.keys(row).forEach(key => {
      if (!dataInstance.hasOwnProperty(key)) dataInstance[key] = row[key];

    });
  });
  return Object.keys(dataInstance).map(fieldName => {
    let stat = getDataStat(data.map(d => d[fieldName]));
    return {
      field: fieldName,
      type: getVegaType(dataInstance[fieldName], fieldName),
      entropy: stat.entropy,
      cardinality: stat.cardinality,
      values: stat.unique,
      min: stat.min,
      max: stat.max
    }
  });
}

export function getVegaType(value, name) {
  if (name == "year") return "nominal";
  if (name == "date") return "temporal";
  if (typeof value == "string") {
    if (!isNaN(Date.parse(value))) return "temporal";
    return "nominal";
  }
  if (typeof value == "number") return "quantitative";
  if (typeof value == "boolean") return "nominal";
}
export function getDataStat(values) {
  // @values: an array
  let uniques = Array.from(new Set(values));
  let total = values.length;
  let rel_count = uniques.map(d => values.filter(e => e == d || e === d).length / total);
  let ent = rel_count.reduce((acc, d) => { return acc - d * Math.log2(d) }, 0)
  return {
    entropy: ent,
    cardinality: uniques.length,
    unique: uniques,
    min: d3.min(values),
    max: d3.max(values)
  }
}
export function deepcopy(object) {
  return JSON.parse(JSON.stringify(object));
}
export const projectionMap = {
  "country": "country",
  "us-county": "counties",
  "us-state": "states"
}
export function range(n) {
  // return 0, 1, 2, ...., n-1
  return Array.from({ length: n }, (_, i) => i)
}
export function setObjectValueCascaded(obj, keySplit, value) {
  // set object value given the list of cascaded keys
  let curr_obj = obj;
  if (keySplit.constructor.name !== "Array") {
    keySplit = keySplit.split(">");
  }
  let oldValue = getObjectValueCascaded(obj, keySplit);
  if (oldValue !== undefined || value !== undefined) {
    for (let ki in keySplit) {
      let key = keySplit[ki];
      if (ki == keySplit.length - 1) {
        if (value === undefined) delete curr_obj[key];
        else curr_obj[key] = value;
      } else {
        if (curr_obj[key] === undefined || curr_obj[key] === null) curr_obj[key] = {};
        curr_obj = curr_obj[key];
      }
    }
  }
  return obj;
}
export function getObjectValueCascaded(obj, keySplit) {
  // get object value given the list of cascaded keys
  if (keySplit.constructor.name !== "Array") {
    keySplit = keySplit.split(">");
  }
  if (obj === undefined || obj === null) {
    return undefined;
  } else if (keySplit.length === 0) {
    return "N/A";
  } else if (keySplit.length === 1) {
    return obj[keySplit[0]];
  } else {
    return getObjectValueCascaded(obj[keySplit[0]], keySplit.slice(1));
  }
}
export function makeTextShadow(color) {
  return ["1.5px 1.5px", "1.5px 0", "1.5px -1.5px", "0 1.5px", "0 -1.5px", "-1.5px 1.5px", "-1.5px 0", "-1.5px -1.5px"].map(f => color + " " + f).join(", ");
}
// expression eval
export function evalFormat(format, value, ftype) {
  if (ftype === "datetime" || ftype === "temporal") return d3.timeFormat(format)(Date.parse(value));
  else {

    if (isTimeFormat(format)) {
      return d3.timeFormat(format)(Date.parse(value));
    } else if (["'%y"].includes(format)) {
      return "'" + d3.timeFormat("%y")(Date.parse(value));
    } else {
      return d3.format(format)(value);
    }
  }
}

export function evalExpression(expr, field, value, data, autoGroupby, config) {
  let constants = `
    let data = ${JSON.stringify(data)};
    let field = "${field}";
    let value = ${typeof value == "string" ? "'" + value + "'" : value};
    let autoGroupby = ${autoGroupby};
  `
  let preEval = constants + `
  function round(n, d) {
    let b = Math.pow(10, d);
    return Math.round(n * b) / b;
  }
  function equal(a, b) {
    return a == b;
  }
  function sum(f, g) {
    if ((g !== undefined && g === true) || (g === undefined && autoGroupby)) { // does group-by
      return data.filter(d => equal(d[field], value)).reduce((acc, cur) => acc + cur[f], 0);
    } else if (g !== undefined && g?.constructor.name === "String") { // does group-by a field
      let _v = data.filter(d => equal(d[field], value))?.[0]?.[g];
      if (_v) {
        return data.filter(d => equal(d[g], _v)).reduce((acc, cur) => acc + cur[f], 0);
      }
      else return 'NaN';
    }  else if (g !== undefined && g?.constructor.name === "Array") { // does group-by a field
      let _vp = data.filter(d => equal(d[field], value))?.[0];
      let _v0 = _vp?.[g[0]], _v1 = _vp?.[g[1]];
      if (_v0 && _v1) {
        return data.filter(d => equal(d[g[0]], _v0) && equal(d[g[1]], _v1)).reduce((acc, cur) => acc + cur[f], 0);
      }
      else return 'NaN';
    } else { // do not group-by
      return data.reduce((acc, cur) => acc + cur[f], 0);
    }
  }
  function count(f, g) {
    if ((g !== undefined && g === true) || (g === undefined && autoGroupby)) { // does group-by
      return data.filter(d => equal(d[field], value)).length;
    } else { // do not group-by
      return data.length;
    }
  }
  function mean(f, g) {
    if ((g !== undefined && g === true) || (g === undefined && autoGroupby)) { // does group-by
      let fil = data.filter(d => equal(d[field], value));
      return fil.reduce((acc, cur) => acc + cur[f], 0) / fil.length;
    } else { // do not group-by
      return data.reduce((acc, cur) => acc + cur[f], 0) / data.length;
    }
  }
  function median(f, g) {
    let vals, n;
    if ((g !== undefined && g === true) || (g === undefined && autoGroupby)) { // does group-by
      vals = data.filter(d => equal(d[field], value)).map(cur => cur[f]);
    } else { // do not group-by
      vals = data.map(cur => cur[f]);
    }
    vals.sort();
    n = vals.length;
    if (n % 2 == 0) return (vals[n / 2 - 1] + vals[n / 2 + 1]) / 2
    else return vals[Math.floor(n / 2)];
  }
  function max(f, g) {
    let vals;
    if ((g !== undefined && g === true) || (g === undefined && autoGroupby)) { // does group-by
      vals = data.filter(d => equal(d[field], value)).map(cur => cur[f]);
    } else { // do not group-by
      vals = data.map(cur => cur[f]);
    }
    vals.sort();
    return vals[0];
  }
  function min(f, g) {
    let vals, n;
    if ((g !== undefined && g ) || (g === undefined && autoGroupby)) { // does group-by
      vals = data.filter(d => equal(d[field], value)).map(cur => cur[f]);
    } else { // do not group-by
      vals = data.map(cur => cur[f]);
    }
    vals.sort();
    n = vals.length;
    return vals[n - 1];
  }
  function argmax(f, g) {
    let vals;
    if ((g !== undefined && g ) || (g === undefined && autoGroupby)) { // does group-by
      vals = data.filter(d => equal(d[field], value));
    } else { // do not group-by
      vals = data;
    }
    vals.sort((a, b) => b[f] - a[f]);
    return vals[0][g];
  }
  function argmin(f, g) {
    let vals;
    if ((g !== undefined && g) || (g === undefined && autoGroupby)) { // does group-by
      vals = data.filter(d => equal(d[field], value));
    } else { // do not group-by
      vals = data;
    }
    console.log(vals);
    vals.sort((a, b) => a[f] - b[f]);
    return vals[0][g];
  }
  function isFirst(f, dt) {
    return equal(config.datatype[f].order?.[0], dt[f]);
  }
  function isLast(f, dt) {
    return equal(config.datatype[f].order?.[config.datatype[f].order?.length - 1], dt[f]);
  }
  `;
  function format(d, fo) {
    return d3.format(fo)(d);
  }
  function timeFormat(d, fo) {
    return d3.utcFormat(fo)(d);
  }
  let formattingStatements = expr.match(/(timeFormat|format)\((([^,]+|(\{(.+:.+(,\s+)?)\})(\.[^,^.]+|\[.+\]))\,\s+['"][^'"]+['"])\)/gi) || [];
  for (const f of formattingStatements) {
    if ((f.startsWith("format(") && f.endsWith(")")) || (f.startsWith("(format(") && f.endsWith("))"))) {
      let s = eval(
        `function format(a, b) {return [a, b]}` + f
      );
      let o = format(s[0], s[1]);
      expr = expr.replace(f, "\"" + o + "\"");
    }
    if ((f.startsWith("timeFormat(") && f.endsWith(")")) || (f.startsWith("(timeFormat(") && f.endsWith("))"))) {
      let s = eval(
        `function timeFormat(a, b) {return [a, b]}` + f
      );
      let o = timeFormat(new Date(s[0]), s[1]);
      expr = expr.replace(f, "\"" + o + "\"");
    }
  }

  if (config?.format !== null && config?.format !== undefined) {
    return d3.format(config.format)(eval(preEval + expr));
  } else {
    return eval(preEval + expr);
  }
}

export function evalTooltipExpression(expr, value) {
  expr = expr.replace(/(datum\.value)/g, "'" + value + "'");
  return eval(expr);
}

// text, legend replacement
export function getReferenceDefaultValues(values, _steps, zero = true) {
  if (!values || values.constructor.name !== "Array") return null;
  let min = Math.min(...values), max = Math.max(...values);
  let range = max - min;
  let has_zero = Math.sign(min) != Math.sign(max);
  let steps = _steps || 4, raw_step, scale_has_zero, afterZero, beforeZero;
  if (has_zero && (min == 0 || max == 0)) { // including zero
    scale_has_zero = true;
    if (min == 0) { // odd
      afterZero = steps;
    } else {
      beforeZero = steps;
    }
    raw_step = range / steps;
  } else if (has_zero) { // including zero
    if (steps % 2 == 1) { // odd
      scale_has_zero = true;
      afterZero = Math.floor(_steps / 2);
      beforeZero = Math.floor(_steps / 2);
    } else if (steps % 2 == 0) { // even
      scale_has_zero = false;
      afterZero = steps / 2;
      beforeZero = steps / 2;
    }
    raw_step = range / steps;
  } else { // not including zero
    scale_has_zero = false;
    if (Math.abs(min) > Math.abs(max)) { // neg scale
      raw_step = min / steps;
      afterZero = 0;
      beforeZero = steps - 1;
    } else { // pos scale
      raw_step = max / steps;
      afterZero = steps - 1;
      beforeZero = 0;
    }
  }
  let step = niceCeilingValue(Math.abs(raw_step));
  let valueBeforeZero = [];
  for (let i = 0; i < beforeZero; i++) {
    valueBeforeZero.push(- (i + (zero ? 0 : 1)) * step);
  }
  valueBeforeZero.reverse();
  let valueAfterZero = [];
  for (let i = 0; i < afterZero; i++) {
    valueAfterZero.push((i + (zero ? 0 : 1)) * step);
  }
  let outcome = [];
  if (scale_has_zero) {
    outcome.push(...valueBeforeZero);
    outcome.push(0);
    outcome.push(...valueAfterZero);
  } else {
    outcome.push(...valueBeforeZero);
    outcome.push(...valueAfterZero);
  }
  return outcome;
}

function n_zeros(n) {
  let result = ""
  for (let i = 0; i < n; i++) {
    result += "0"
  }
  return result;
}
export function niceCeilingValue(v) {
  let nices = [1, 25, 50];
  if (v <= 1) {
    if (v <= 0.25) return 0.25;
    else if (v <= 0.5) return 0.5;
    else if (v <= 0.75) return 0.75;
    else return 1;
  } else {
    let digits = v.toString().split(".")[0];
    let len_digits = digits.length;
    let ref_digit = parseInt(digits[0] + digits[1]);
    if (v <= 2.5) {
      return 2.5;
    } else if (v <= 5) {
      return 5;
    } else if (ref_digit <= 10) {
      return parseInt("10" + n_zeros(len_digits - 2))
    } else if (ref_digit <= 25) {
      return parseInt("25" + n_zeros(len_digits - 2))
    } else if (ref_digit <= 50) {
      return parseInt("50" + n_zeros(len_digits - 2))
    } else {
      return parseInt("10" + n_zeros(len_digits - 1))
    }
  }
}

export function copyLegend(placeSelector, replacer, viewSpec, dblClick) {
  let legend_type = "discrete";
  let parent = document.querySelector(placeSelector.split(" ")[0]);
  let chart_width = document.querySelector(placeSelector).getBoundingClientRect().width; /* viewSpec.width */
  chart_width -= 48; // discounting vega detail
  // let chart_height = viewSpec.height, rows = 1;
  // if (viewSpec.encoding?.facet !== undefined) {
  //   let cols = viewSpec.encoding?.facet.columns || 1;
  //   let vals = viewSpec.datasets.values.map(d => d[viewSpec.encoding?.facet.field]);
  //   rows = Math.ceil(Array.from(new Set(vals)).length / cols);
  // } else if (viewSpec.concat !== undefined) {
  //   let cols = viewSpec.columns || 1;
  //   rows = Math.ceil(viewSpec.concat.length / cols);
  // }
  // chart_height = chart_height * rows;
  let chart_height = parent.offsetHeight;
  let Larea;
  document.querySelectorAll(placeSelector + " svg g[aria-roleDescription=legend]").forEach(label_item_group => {
    let legend_groups = d3.select(label_item_group).selectAll("g.mark-group.role-scope>g");
    if (legend_groups.nodes().length == 0) {
      legend_groups = d3.select(label_item_group).selectAll("g.mark-group.role-legend-entry>g");
      if (legend_groups.nodes().length > 0) legend_type = "gradient";
    }
    let ariaLabel = label_item_group.getAttribute("aria-label");
    let legendInfo = ariaLabel.match(/\s\(Layer\:\s(F\_layer\_.+|F\_geoLayer\_.+|view\_[0-9a-zA-Z]+\_(.+\_)?child|null),\sChannel\:\s.+\)$/i)[0];
    if (legendInfo) legendInfo = legendInfo.trim().replace(/^\(/, "").replace(/\)$/, "").replace(/\s/gi, "").split(",").map(d => d.trim().split(":"));
    let legendLayer, legendChannel;
    legendInfo?.forEach(d => {
      if (d[0] === "Layer") legendLayer = d[1]
      else if (d[0] === "Channel") legendChannel = d[1]
    });
    // if (legendLayer) legendLayer = legendLayer.replace("(Layer: ", "").replace(/\)$/i, "");
    let legend_positioners = legend_groups.nodes().map(d => {
      return parseTransform(d.getAttribute("transform")).map(e => parseTransformStatement(e)).filter(f => f[0] === "translate")[0];
    });
    let legend_translates = {
      "x": legend_positioners.map(t => t[1]),
      "y": legend_positioners.map(t => t[2])
    }
    let legendBbox = label_item_group.getBBox();
    if (d3.select(placeSelector + " .chart-wrapper svg>g").attr("transform")) {
      let translates = d3.select(placeSelector + " .chart-wrapper svg>g").attr("transform").split(/\s+/g).filter(t => t.startsWith("translate"));
      if (translates[0] !== undefined) {
        let translate_splits = translates[0].split(/[\(\),]/g);
        legendBbox.x += parseFloat(translate_splits[1])
        legendBbox.y += parseFloat(translate_splits[2])
      }
    }
    let direction = "default";
    if (legend_translates.x.every(d => d == d3.mean(legend_translates.x))) direction = "vertical";
    if (legend_translates.y.every(d => d == d3.mean(legend_translates.y))) direction = "horizontal";
    if (legend_type !== "gradient" && direction !== "vertical") direction = "horizontal";
    let original_legend_box_width = label_item_group.getBoundingClientRect().width
    let box_adjusted_width = Math.min(chart_width, Math.max((direction === "horizontal" ? chart_width - legendBbox.x : 0), original_legend_box_width));
    Larea = d3.select(placeSelector).append("div");
    let layerId;
    if (legendLayer === "null") {
      Larea.attr("data-layer", "0");
    } else if (legendLayer.startsWith("F_geoLayer_")) {
      Larea.attr("data-layer", "geoLayer_" + legendLayer.match(/[0-9]+/i)[0]);
    } else if (!legendLayer.startsWith("F_layer")) {
      Larea.attr("data-layer", "0");
    } else if ((layerId = legendLayer.match(/[0-9]+/i)?.[0], layerId)) {
      Larea.attr("data-layer", layerId);
    }
    if (legendChannel) Larea.attr("data-channel", legendChannel);
    // todo: add class name!
    Larea.style("width", box_adjusted_width + "px");
    Larea.attr("data-role", "legend-replacement")
    if (legend_type != "gradient" || direction === "horizontal") {
      Larea.style("display", "flex");
      if (direction === "horizontal") Larea.style("align-items", "center");
      // if (original_legend_box_width > box_adjusted_width * 1.5)
      Larea.style("flex-wrap", "wrap");
    }
    let legend_title = d3.select(label_item_group).select("g.mark-text.role-legend-title>text");
    if (legend_title.node()) {
      let title = Larea.append("div")
      title.text(legend_title.node().textContent);
      title.attr('class', 'role-replaced-legend-label');
      if (direction === "horizontal") title.style("width", "100%");
      if (legend_title.attr("font-family")) title.style("font-family", legend_title.attr("font-family"));
      if (legend_title.attr("font-size")) title.style("font-size", legend_title.attr("font-size"));
      if (legend_title.attr("opacity")) title.style("opacity", legend_title.attr("opacity"));
      if (legend_title.attr("fill")) title.style("fill", legend_title.attr("fill"));
      if (legend_title.attr("font-weight")) title.style("font-weight", legend_title.attr("font-weight"));
    }

    let legend_mark_width_max = d3.max(legend_groups.nodes().map(group => {
      let mark = d3.select(group).select("g.role-legend-symbol");
      if (mark.node() == null && legend_type == "gradient") mark = d3.select(group).select("g.role-legend-gradient");
      let bbox = (mark.node().children[0].getBBox() || mark.node().getBBox());
      return bbox.width;
    }));

    legend_groups.nodes().forEach(group => {
      let mark = d3.select(group).select("g.role-legend-symbol");
      if (mark.node() == null && legend_type == "gradient") mark = d3.select(group).select("g.role-legend-gradient");
      let text = d3.select(group).select("g.role-legend-label text");
      if (legend_type == "gradient") text = d3.select(group).selectAll("g.role-legend-label text");
      let bbox = (mark.node().children[0].getBBox() || mark.node().getBBox());
      let mark_width = (direction === "vertical" ? legend_mark_width_max : bbox.width);
      let row = Larea.append("div")
      if (legend_type != "gradient") {
        row.style("display", "flex");
        // if (direction === "horizontal")
        row.style("align-items", "center");
        row.style("margin-right", "15px");
      }
      let svg = row.append("svg");
      let label = row.append("div");
      label.attr("class", "role-replaced-legend-label")
      label.attr("data-direction", direction);
      svg.attr("width", mark_width + 7).attr("height", (bbox.height == 0 ? 5 : bbox.height + 2));
      // svg.style("margin-top", "5px");
      // svg.style("margin-right", "5px");
      svg.html(mark.node().innerHTML);
      let isInine = svg.select("path").node().getTotalLength() == bbox.width;
      if (isInine) {
        svg.select("path").attr("transform", "translate(" + ((mark_width + 2) / 2) + "," + ((bbox.height == 0 ? 5 : bbox.height + 2) / 2) + ")")
      } else if (svg.select("path").attr("transform")?.startsWith("translate")) {
        svg.select("path").attr("transform", "translate(" + ((mark_width + 2) / 2) + "," + ((bbox.height + 2) / 2) + ")")
      }
      if (legend_type !== "gradient") {
        label.text(text.node().textContent);
        if (text.attr("font-family")) label.style("font-family", text.attr("font-family"));
        if (text.attr("font-size")) label.style("font-size", text.attr("font-size"));
        if (text.attr("opacity")) label.style("opacity", text.attr("opacity"));
        if (text.attr("fill")) label.style("fill", text.attr("fill"));
        if (text.attr("font-weight")) label.style("font-weight", text.attr("font-weight"));
        // if (direction === "vertical") label.style("transform", "translateY(" + (bbox.height / 2 - parseFloat(text.attr("font-size")) / 2) + "px)")
      } else {
        // horizontal 
        let labelItems = label.selectAll("div").data(text.nodes()).enter().append("div")
        labelItems.text(d => d.textContent).style("position", "absolute").style("transform", d => d3.select(d).style("transform").replace(/\, [0-9]+\)/i, ", 0)"));
        labelItems.attr("original-alignment", d => d3.select(d).attr("text-anchor"));
        label.style("transform", "translateX(-10px)");
        // to do : vertical
      }
    });

    // let hvalues = []
    // Larea.selectAll("div")._groups[0].forEach(d => hvalues.push(d.offsetHeight || 0))
    // let amount_h_adjust = hvalues.f$max();
    // let height_adjust = parseFloat(Larea.node().getClientRects()[0].height) - amount_h_adjust;
    if (legendBbox.y < 0) {
      document.querySelector(placeSelector).style.marginTop = (parseFloat(document.querySelector(placeSelector).style.marginTop || 0) - legendBbox.y) + "px"
    } else if (legendBbox.y + legendBbox.height > chart_height) {
      // if (height_adjust > 0 && legendBbox.y > chart_height / 2) {
      //   legendBbox.y -= d3.min(legendBbox.y, height_adjust);
      //   document.querySelector(placeSelector).style.marginTop = (parseFloat(document.querySelector(placeSelector).style.marginTop || 0) - height_adjust) + "px"
      // } else if (height_adjust < 0) {
      let ha = legendBbox.y + legendBbox.height - chart_height;
      document.querySelector(placeSelector).style.marginBottom = (parseFloat(document.querySelector(placeSelector).style.marginBottom || 0) + ha) + "px"
    } else
      Larea.style("position", "absolute").style("left", legendBbox.x + "px").style("top", legendBbox.y + "px");
    label_item_group.setAttribute("opacity", 0);

    // event
    Larea.on("dblclick", (event) => { dblClick(event); });
  });
  // text replacement 
  let textElements = d3.selectAll(placeSelector + " .role-replaced-legend-label").nodes();
  if (legend_type == "gradient") {
    textElements = d3.selectAll(placeSelector + " .role-replaced-legend-label>div").nodes();
    let title = d3.selectAll(placeSelector + " .role-replaced-legend-label").nodes().filter(d => d.textContent.endsWith('-title'));
    textElements.push(...title);
  }
  Object.keys(replacer).forEach(replacerKey => {
    if (!replacerKey.startsWith('!l-')) return null;
    let replacedItems = bestMatchTextReplacing(textElements, replacerKey);
    replacedItems?.forEach(replaced => {
      if (replacer[replacerKey].html) replaced.innerHTML = replacer[replacerKey].html;
      if (replacer[replacerKey].name) replaced.parentElement.setAttribute("class", replacer[replacerKey].name);
      let direction = replaced.getAttribute("data-direction") || "default";
      if (replaced.children[0].getAttribute('data-gap')) {
        let gap = replaced.children[0].getAttribute('data-gap');
        if (direction === "vertical" || direction === "default") replaced.parentElement.style.marginBottom = (gap.constructor.name === "Number" ? gap : gap + "px");
        if (direction === "horizontal" || direction === "default") replaced.parentElement.style.marginRight = (gap.constructor.name === "Number" ? gap : gap + "px");
      } else {
        if (direction === "vertical" || direction === "default") replaced.parentElement.style.marginBottom = "5px";
        if (direction === "horizontal" || direction === "default") replaced.parentElement.style.marginRight = "5px";
      }
      if (legend_type == "gradient") {
        replaced.children[0].style.textAlign = { "start": "left", "middle": "center", "end": "right" }[replaced.getAttribute("original-alignment")];
      }
      if (replaced.children[0].getAttribute("data-ignroe-vl-position") === "top") {
        Larea.style("width", chart_width + "px");
        Larea.style("flex-wrap", "no-wrap");
        Larea.style("left", 0);
      }
      if (replacer[replacerKey].event) {
        if (replacer[replacerKey].event.on) {
          replaced.addEventListener(replacer[replacerKey].event.on.type, replacer[replacerKey].event.on.function);
        }
        if (replacer[replacerKey].event.off) {
          replaced.addEventListener(replacer[replacerKey].event.off.type, replacer[replacerKey].event.off.function);
        }
      }
    });
  });

}
export function textReplacement(placeSelector, replacer, dblClick) {
  document.querySelectorAll(placeSelector + " [data-role=text-replacement]").forEach(item => item.remove());
  let placeClientBox = document.querySelector(placeSelector).getClientRects()[0];
  let textElements = d3.selectAll(placeSelector + " g.mark-text text").nodes();
  textElements.forEach(replaced => {
    let replacerKey = bestMatchTextReplacingClass(replaced, replacer);
    if (replacerKey == null) {
      let blankCat = d3.select(replaced)?.attr("class")?.split(" ")
      if (blankCat) {
        let blankCreator = replacer[blankCat[0].split("-").slice(0, 2).join("-") + "-blank"]?.["creator"]
        if (blankCreator && blankCat) {
          let blankValue = blankCat[1], blankIndex = blankCat[2];
          blankCreator(blankValue, blankIndex);
        }
      }
    }
    replacerKey = bestMatchTextReplacingClass(replaced, replacer);
    if (replacerKey?.startsWith('!l-')) return null;
    if (replacerKey?.endsWith('-blank')) return null;
    if (replacerKey !== null) {
      let replaced_angle = parseTransform(replaced.getAttribute("transform")).filter(t => t.startsWith('rotate'))[0];
      let class_name = replacer[replacerKey].name;
      let replaced_bbox = replaced.getBBox();
      let origin = getCascadedOrigin(replaced, false);
      let anchor = replaced.getAttribute('text-anchor');
      if (!anchor) anchor = "start";
      // setting
      let replacing = document.createElement("replacement");
      // todo: add class name
      replacing.setAttribute("data-role", "text-replacement")
      replacing.style.position = "absolute";
      let role = replacerKey.split("-")[1]
      if (["row", "x", "x2", "column", "y", "y2"].includes(role)) replacing.setAttribute("class", "fronto-label-axis-" + role + " " + class_name);
      else replacing.setAttribute("class", "fronto-label-" + role + " " + class_name);
      if (!replacing.style.lineHeight) replacing.style.lineHeight = '100%';
      if (anchor === "end") {
        replacing.style.transform += " translateX(-100%)";
      }
      else if (anchor === "middle") {
        replacing.style.transform += " translateX(-50%)";
      };
      let widthSet = false;
      if (replacer[replacerKey].html) replacing.innerHTML = replacer[replacerKey].html;
      Array.from(replacing.children?.[0]?.children)?.forEach((childChild, ci) => {
        if (childChild.getAttribute("data-text-color") === "$mark") {
          replacing.children[0].children[ci].style.color = replaced.getAttribute("fill") || replaced.getAttribute("stroke");
        }
        if (childChild.style.width.length > 0) widthSet = true;
      });
      if (replacing.children?.[0]?.getAttribute("data-dx") !== null) {
        replacing.style.transform += " translateX(" + replacing.children[0].getAttribute("data-dx") + "px)"
      }
      if (replacing.children?.[0]?.getAttribute("data-dy") !== null) {
        replacing.style.transform += " translateY(" + replacing.children[0].getAttribute("data-dy") + "px)"
      }
      if (!widthSet) {
        let maxWidth = 0;
        Array.from(replacing.children?.[0]?.children)?.forEach((childChild, ci) => {
          let textWidth = getDefaultBoxWidth(childChild.textContent, parseFloat(childChild.style.fontSize) || undefined);
          if (textWidth > maxWidth) maxWidth = textWidth;
        });
        if (maxWidth) {
          replacing.style.width = maxWidth + "px";
        }
      }
      // Keep looking at this part (angle-rotate)
      if (replacerKey.startsWith('$-y-') || replacerKey.startsWith('$-row-')) {
        replacing.style.top = (origin[1] - parseFloat(replacing.children[0].children[0]?.style.fontSize || 16) / 2) + 'px';
        // replacing.style.top = (origin[1] - parseFloat(replacing.children[0].children[0].style.fontSize || 16) * 0.25) + 'px';
        replacing.style.transform += " translateY(-50%)";
        replacing.style.left = origin[0] + 'px';
        if (replaced_angle !== undefined) {
          if (origin[0] < placeClientBox.width - origin[0]) replacing.style.transformOrigin = "center right";
          else replacing.style.transformOrigin = "center left";
          replacing.style.transform = replacing.style.transform + " " + replaced_angle.replace(")", "deg)");
        }
      }
      else if (replacerKey.startsWith('$-x-')) {
        let isSerial = replacing.children[0]?.getAttribute("data-serial");
        if (isSerial === "serial-vertical") replacing.style.top = 0;
        else replacing.style.top = (origin[1] - parseFloat(replacing.children[0]?.children[0]?.style.fontSize || 16)) + 'px';
        replacing.style.left = (origin[0]) + 'px';
        let width2 = Array.from(replacing.children[0].children)?.reduce((acc, e) => { return acc + parseFloat(e.style.width) }, 0) / 2;
        if (replaced_angle !== undefined) {
          if (anchor === "middle") {
            replacing.style.transformOrigin = "center center";
            replacing.style.transform += " translateX(50%)";
          } else if (anchor === "start") {
            replacing.style.transformOrigin = "center center";
            replacing.style.transform += " translateX(50%)";
          } else {
            replacing.style.transformOrigin = "right center";
          }
          if (isSerial === "serial-vertical") {
            if (!width2) {
              replacing.style.width = "150px";
              if (!replacing.style.textAlign) replacing.style.textAlign = "right";
              else if (replacing.style.textAlign) replacing.style.textAlign = "right";
            }
            replacing.style.transform = replacing.style.transform + " translateY(" + (width2 ? width2 + "px" : "75px") + ")";
          } else if (parseFloat(replacing.style.top) < placeClientBox.height - parseFloat(replacing.style.top)) {
            replacing.style.transform = replacing.style.transform + " translateY(" + (width2 ? width2 + "px" : "100%") + ")";
          } else replacing.style.transform = replacing.style.transform + " translateY(" + (width2 ? width2 + "px" : "100%") + ")";
          replacing.style.transform = replacing.style.transform + " " + replaced_angle.replace(")", "deg)");
        }
      }
      else {
        replacing.style.top = (origin[1] - parseFloat(replacing.children[0]?.children[0]?.style.fontSize || 12)) + 'px';
        replacing.style.left = origin[0] + 'px';
        if (replaced_angle !== undefined) {
          replacing.style.transformOrigin = "0 0";
          let angle_value = parseFloat(replaced_angle.match(/[0-9]+/gi)?.[0] || "");
          if (angle_value > 0 && angle_value < 180) replacing.style.transformOrigin = "-15px 15px";
          replacing.style.transform = replacing.style.transform + " " + replaced_angle.replace(")", "deg)");
        }
      }
      document.querySelector(placeSelector).appendChild(replacing)
      if (replacerKey.startsWith('$-x-')) {
        if (!replaced_angle) {
          replacing.style.top = (origin[1] - parseFloat(replacing.children[0].children[0].style.fontSize)) + 'px';
          if (replacing.clientWidth > replaced_bbox.width) {
            let xgap = replacing.clientWidth - replaced_bbox.width;
            replacing.style.left = (origin[0] + xgap / 2) + 'px';
          }
        }
      }
      //alignment (row/y axis)
      if (["row", "y", "y2"].includes(role) && replacing.children[0].children[0]?.style.textAlign === "left") {
        let leftMost = 90000000;
        document.querySelectorAll(placeSelector + " .fronto-label-axis-" + role).forEach(node => {
          if (leftMost > parseFloat(node.style.left)) leftMost = parseFloat(node.style.left);
        });
        document.querySelectorAll(placeSelector + " .fronto-label-axis-" + role).forEach(node => {
          node.style.left = leftMost + "px";
        })
      }
      else if (["row", "y", "y2"].includes(role) && replacing.children[0].children[0]?.style.textAlign === "right") {
        let rightMost = 0;
        document.querySelectorAll(placeSelector + " .fronto-label-axis-" + role).forEach(node => {
          if (rightMost < parseFloat(node.style.left)) rightMost = parseFloat(node.style.left);
        });
        document.querySelectorAll(placeSelector + " .fronto-label-axis-" + role).forEach(node => {
          node.style.left = rightMost + "px";
        })
      }
      // orient custom
      if (replacing.children[0].getAttribute("data-orient") !== null) {
        if (replacing.children[0].getAttribute("data-orient") === "end") {
          replacing.style.transform = replacing.style.transform + " translateX(-80%)";
        } else if (replacing.children[0].getAttribute("data-orient") === "start") {
          replacing.style.transform = replacing.style.transform + " translateX(100%)";
        }
      }
      // consider angle! (todo)
      replaced.setAttribute("opacity", 0)
    }
  });
  let replacements = Array.from(document.querySelectorAll(placeSelector + " replacement"));
  replacements?.forEach(replItem => {
    replItem.addEventListener("dblclick", (event) => { dblClick(event) });
    if (replItem.classList.value.startsWith('fronto-label-axis-')) return '';
    if (replItem.children?.[0].getAttribute("data-numbering") === "true") {
      let newStyle = replItem.children?.[0].getAttribute("style");
      if (newStyle == null) newStyle = "";
      // newStyle = newStyle;
      replItem.children?.[0].setAttribute("style", newStyle);
    }
    if (replItem.children?.[0].getAttribute("data-callout") === "yes") {
      function mapMatched(map1, map2) {
        let mapIter = map1.entries();
        let score = 0;
        for (let i = 0; i < map1.size; i++) {
          let entry1 = mapIter.next().value;
          if (entry1[1] == map2.get(entry1[0])) score++;
        }
        return score;
      }
      function getMatchedDatumMark(selectedDatum, viewname) {
        // match the data item description & the actual mark
        let dataMarks = d3.selectAll(placeSelector + " svg g.role-mark *[aria-label]");
        let maxScore = 0,
          maxItem; // score based
        let maxByMark = true;
        dataMarks.each((d) => {
          let score = 0;
          let d_datum = new Map(Object.keys(d.datum).map((key) => [key, d.datum[key]]));
          let s_datum = new Map(Object.keys(selectedDatum.datum).map((key) => [key, selectedDatum.datum[key]]));
          let datum_match_score = mapMatched(d_datum, s_datum);
          score = datum_match_score;
          if (datum_match_score > 0) {
            maxByMark = false;
          }
          if (d.ariaRoleDescription === selectedDatum.role) score++; // mark type
          if (maxScore < score) {
            // updating max
            maxScore = score;
            maxItem = d;
          }
        });
        if (maxByMark) {
          // when the max score is only through matching mark --> undefined
          return undefined;
        }
        return maxItem;
      }
      function separatedrange(d1, d2) {
        let minD1 = d3.min(d1),
          maxD1 = d3.max(d1),
          minD2 = d3.min(d2),
          maxD2 = d3.max(d2);
        if (minD1 > maxD2 || maxD1 < minD2) return true;
        else return false;
      }
      let padding = parseFloat(replItem.children?.[0].getAttribute("data-callout-offset")) || 5;
      let datum = JSON.parse(replItem.children?.[0].getAttribute("data-datum"));
      let marktype = replItem.children?.[0].getAttribute("data-mark");
      let viewname = replItem.children?.[0].getAttribute("data-viewname");

      let strokeColor = replItem.children?.[0].getAttribute("data-callout-color") || "black";
      let strokeOpacity = parseFloat(replItem.children?.[0].getAttribute("data-callout-width")) || 1;
      let strokeDash = replItem.children?.[0].getAttribute("data-callout-width")?.split(",") || null;
      let strokeWidth = parseFloat(replItem.children?.[0].getAttribute("data-callout-width")) || 1;

      if (placeSelector.includes(viewname)) {
        let anchorTo = getMatchedDatumMark({ datum: datum, role: marktype }, viewname);
        let dataBBox = deepcopy(anchorTo.bounds),
          textBBox = replItem.getBoundingClientRect(),
          viewLayer = d3.select(placeSelector + " .chart-wrapper");
        let textOrigin = viewLayer.node().getBoundingClientRect();
        let graphicOrigin = d3
          .select("#view-" + viewname + " svg g")
          .attr("transform")
          .split(/[\)\(,]/g)
          .slice(1, 3)
          .map((d) => +d);
        textBBox.x -= textOrigin.x;
        textBBox.y -= textOrigin.y;
        let position = [],
          width,
          height;

        let text8pos = get8pos(textBBox.x, textBBox.y, textBBox.width, textBBox.height);

        let dx1 = dataBBox.x1,
          dx2 = dataBBox.x2,
          dy1 = dataBBox.y1,
          dy2 = dataBBox.y2;

        let textYs = [textBBox.y, textBBox.y + textBBox.height],
          dataYs = [dy1 + graphicOrigin[1], dy2 + graphicOrigin[1]],
          textXs = [textBBox.x, textBBox.x + textBBox.width],
          dataXs = [dx1 + graphicOrigin[0], dx2 + graphicOrigin[0]];
        let sepY = separatedrange(textYs, dataYs),
          sepX = separatedrange(textXs, dataXs);
        let orient_;
        if (sepY && sepX) {
          orient_ = "o";
          let dataPosition = [Math.min(dx1, dx2) + graphicOrigin[0], Math.min(dy1, dy2) + graphicOrigin[1]],
            dataWidth = Math.abs(dx1 - dx2),
            dataHeight = Math.abs(dy1 - dy2);
          let data8pos = get8pos(dataPosition[0], dataPosition[1], dataWidth, dataHeight);
          let xy = getClosest8Pos(text8pos, data8pos);
          let x1 = xy[0][0],
            x2 = xy[1][0],
            y1 = xy[0][1],
            y2 = xy[1][1];
          position = [Math.min(x1, x2), Math.min(y1, y2)];
          width = Math.abs(x1 - x2);
          height = Math.abs(y1 - y2);
          // if the callout line is not straight but the angle is defined within 15 pixels of height or width --> make it straight
          if (height < 15) {
            height = 1;
            width = width - padding * 2;
            position[0] = position[0] + padding;
            position[1] = Math.max(y1, y2);
          } else if (width < 15) {
            width = 1;
            heigth = height - padding * 2;
            position[1] = position[1] + padding;
            position[0] = Math.max(x1, x2);
          }
          let calloutObject = viewLayer
            .append("svg")
            .attr("data-role", "replacement-callout")
            .attr("width", width)
            .attr("height", height)
            .style("position", "absolute")
            .style("left", position[0] + "px")
            .style("top", position[1] + "px");
          calloutObject
            .append("line")
            .attr("x1", 0)
            .attr("x2", width == 1 ? 0 : width)
            .attr("y1", 0)
            .attr("y2", height == 1 ? 0 : height)
            .attr("stroke", strokeColor)
            .attr("opacity", strokeOpacity)
            .attr("stroke-width", strokeWidth);
          if (strokeDash) calloutObject.attr("stroke-dasharray", strokeDash.join(" "));
        } else if (sepY) {
          orient_ = "v";
          let dataPosition = [Math.min(dx1, dx2) + graphicOrigin[0], Math.min(dy1, dy2) + graphicOrigin[1]],
            dataWidth = Math.abs(dx1 - dx2),
            dataHeight = Math.abs(dy1 - dy2);
          let data8pos = get8pos(dataPosition[0], dataPosition[1], dataWidth, dataHeight);
          let xy = getClosest8Pos(text8pos, data8pos);
          let x1 = xy[0][0],
            x2 = xy[1][0],
            y1 = xy[0][1],
            y2 = xy[1][1];
          let x = textBBox.x + textBBox.width / 2,
            textY_cand = [text8pos[1][1], text8pos[5][1]],
            dataY_cand = [dy1 + graphicOrigin[1], dy2 + graphicOrigin[1]];
          position = [Math.min(x1, x2), Math.min(y1, y2)];
          width = Math.abs(x1 - x2);
          height = Math.abs(y1 - y2);
          let comb = [
            [textY_cand[0], dataY_cand[0]],
            [textY_cand[0], dataY_cand[1]],
            [textY_cand[1], dataY_cand[0]],
            [textY_cand[1], dataY_cand[1]],
          ].map((combination) => {
            return {
              c: combination,
              d: Math.abs(combination[0] - combination[1]),
            };
          });
          height = d3.min(comb.map((c) => c.d));
          let yS = comb.filter((c) => c.d == width)[0]?.c || comb[3].c;
          let ypos = d3.min(yS);
          ypos = ypos + padding * 2;
          height = height - padding * 2;
          // make callout lines
          let calloutObject = viewLayer
            .append("svg")
            .attr("data-role", "replacement-callout")
            .attr("width", 1)
            .attr("height", height)
            .style("position", "absolute")
            .style("left", x + "px")
            .style("top", ypos + "px");
          calloutObject.append("line").attr("x1", 0).attr("x2", 0).attr("y1", 0).attr("y2", height).attr("stroke", "black");
        } else if (sepX) {
          orient_ = "h";
          let y = textBBox.y + textBBox.height / 2,
            textX_cand = [text8pos[3][0], text8pos[7][0]],
            dataX_cand = [dx1 + graphicOrigin[0], dx2 + graphicOrigin[0]];
          let comb = [
            [textX_cand[0], dataX_cand[0]],
            [textX_cand[0], dataX_cand[1]],
            [textX_cand[1], dataX_cand[0]],
            [textX_cand[1], dataX_cand[1]],
          ].map((combination) => {
            return {
              c: combination,
              d: Math.abs(combination[0] - combination[1]),
            };
          });
          width = d3.min(comb.map((c) => c.d));
          let xS = comb.filter((c) => c.d == width)[0].c;
          let xpos = d3.min(xS);
          if (xpos == xS[0]) {
            xpos = xpos + padding * 2;
          } else {
            width = Math.max(0, width - padding * 2);
          }
          // make callout lines
          let calloutObject = viewLayer
            .append("svg")
            .attr("data-role", "replacement-callout")
            .attr("width", width)
            .attr("height", 1)
            .style("position", "absolute")
            .style("left", xpos + "px")
            .style("top", y + "px");
          calloutObject.append("line").attr("x1", 0).attr("x2", width).attr("y1", 0).attr("y2", 1).attr("stroke", "black");
        }
      }
    }

    let xAxisReplacements = Array.from(document.querySelectorAll(placeSelector + " replacement.fronto-label-axis-x"));
    if (xAxisReplacements.every(replItem => { return replItem.style.transform.indexOf("rotate(270deg)") >= 0 })) {
      let xAxisLabelWidths = xAxisReplacements?.map(replItem => { return replItem.getBoundingClientRect().width });
      let maxWidths = d3.max(xAxisLabelWidths);
      xAxisReplacements?.forEach(replItem => {
        if (replItem.getBoundingClientRect().width < maxWidths) {
          replItem.style.height = maxWidths + "px";
          replItem.children[0].style.transform = "translateY(50%)";
        }
      })
    }
  });
}
export function overlapingRect(rect1, rect2, rel) { // @rect1, rect2: DOMRect (getBoundingClientRect)
  let a = [[rect1.x, rect1.y], [rect1.x + rect1.width, rect1.y + rect1.height]];
  let b = [[rect2.x, rect2.y], [rect2.x + rect2.width, rect2.y + rect2.height]];
  let areaA = rect1.width * rect1.height,
    areaB = rect2.width * rect2.height;
  let distX = Math.min(a[1][0], b[1][0]) - Math.max(a[0][0], b[0][0]),
    distY = Math.min(a[1][1], b[1][1]) - Math.max(a[0][1], b[0][1]);
  let overlap = 0;
  if (distX > 0 && distY > 0) overlap = distX * distY;
  if (rel === true) {
    return Math.max(overlap / areaA, overlap / areaB);
  }
  return overlap
}
export function xOverlap(rect1, rect2, rel) { // @rect1, rect2: DOMRect (getBoundingClientRect)
  let a = [[rect1.x, rect1.y], [rect1.x + rect1.width, rect1.y + rect1.height]];
  let b = [[rect2.x, rect2.y], [rect2.x + rect2.width, rect2.y + rect2.height]];
  let distX = Math.min(a[1][0], b[1][0]) - Math.max(a[0][0], b[0][0]);
  return distX > 0;
}
export function yOverlap(rect1, rect2, rel) { // @rect1, rect2: DOMRect (getBoundingClientRect)
  let a = [[rect1.x, rect1.y], [rect1.x + rect1.width, rect1.y + rect1.height]];
  let b = [[rect2.x, rect2.y], [rect2.x + rect2.width, rect2.y + rect2.height]];
  let distY = Math.min(a[1][1], b[1][1]) - Math.max(a[0][1], b[0][1]);
  return distY > 0;
}
export function parseTransform(transformText) {
  return transformText.split(" ");
}
export function parseTransformStatement(transformText) {
  return transformText.split(/[\(\),\s]/g);
}
export function parseTransformVale(transformText) {
  return transformText.split(/[\(\),\s]/g).slice(1);
}
export function bestMatchTextReplacing(textElements, replacerKey) {
  let first = textElements.filter(orgDom => orgDom.textContent === replacerKey);
  if (first.length > 0) return first;
  else {
    let scores = textElements.map(orgDom => {
      let functionalityCheck = true;
      if (replacerKey.startsWith("$-")) {
        functionalityCheck = orgDom.textContent.startsWith("$-") && replacerKey.startsWith(orgDom.textContent.split("-").slice(0, 2).join("-"));
      } else if (replacerKey.startsWith("!l")) {
        functionalityCheck = orgDom.textContent.startsWith("!l");
      }
      let exactMatch = true;
      if (replacerKey.includes("!@") && orgDom.textContent.includes("!@")) {
        exactMatch = replacerKey.replace(/\!@+/g, "") === orgDom.textContent.replace(/\!@+\…?/g, "")
      }
      if (!functionalityCheck) return 0;
      if (!exactMatch) return 0;
      return replacerKey.matchFromStart(orgDom.textContent);
    });
    if (!scores.every(d => d == 0)) {
      let argMax = scores.argMax(null, true);
      if (argMax.length > 1) return null;
      return textElements.filter((val, ind) => argMax.includes(ind));
    } else {
      return null;
    }
  }
}
export function bestMatchTextReplacingClass(textElement, replacer) {
  let first = Object.keys(replacer).filter(replacerKey => {
    return d3.select(textElement).attr("class")?.split(" ")[0] === replacerKey;
  });
  if (first.length > 0) return first[0];
  else return null;
}

// positioning
export function getCascadedOrigin(svgDOM, skipFirst) { // needs fixing!
  if (svgDOM === null || svgDOM === undefined || svgDOM.tagName === "svg") {
    return [0, 0];
  } else {
    let dom = svgDOM._svg ? d3.select(svgDOM._svg) : d3.select(svgDOM);
    let parent = dom.node().parentElement;
    let parentOrigin = getCascadedOrigin(parent, false);
    if (dom.attr("transform")?.startsWith("translate")) {
      let tempOrigin;
      if (!skipFirst) {
        tempOrigin = dom
          .attr("transform")
          .split(/[\)\(,]/g)
          .slice(1, 3)
          .map((d) => +d);
      } else {
        tempOrigin = [0, 0];
      }
      return [parentOrigin[0] + tempOrigin[0], parentOrigin[1] + tempOrigin[1]];
    } else return parentOrigin;
  }
}

// Other utilities
export function setObjectValue(org, alt) {
  if (alt?.constructor.name === "Object") {
    Object.keys(alt).forEach(altKey => {
      org[altKey] = setObjectValue(org[altKey], alt[altKey]);
    });
  } else { // non-object
    return alt;
  }
  return org;
}
export function round(n, d) {
  let b = Math.pow(10, d);
  return Math.round(n * b) / b;
}
export function toStringD(a) {
  if (a.constructor.name === "Array") return this.map(d => d.toStringD()).join(',');
  else if (a.constructor.name === "Date" && a != "Invalid Date") {
    return a.getTime().toString();
  } else {
    return a.toString();
  }
}
export function unitString(n, defUnit) {
  let unit = defUnit || "px"; // default unit
  if (n.toString().endsWith("%")) unit = "%";
  else if (n.toString().endsWith("rem")) unit = "rem";
  else if (n.toString().endsWith("em")) unit = "em";
  else if (n.toString().endsWith("in")) unit = "in";
  else if (n.toString().endsWith("cm")) unit = "cm";
  else if (n.toString().endsWith("mm")) unit = "mm";
  else if (n.toString().endsWith("deg")) unit = "deg";
  if (parseFloat(n) == 0) return 0;
  else if (unit === "") return parseFloat(n);
  else return parseFloat(n) + unit;
}
export function arrayIdentity(arr, brr) {
  if (arr.length !== brr.length) return false;
  else {
    for (const i in arr) {
      if (arr[i] != brr[i]) return false;
    }
    return true;
  }
}
export function unique(a) {
  return Array.from(new Set(a));
}

// filtering
export function toFilterString(fObj) {
  function makeAtomicStatement(fieldname, elem, op) {
    if (op === "eq") {
      if (typeof elem == "string") return "datum." + fieldname + " == '" + elem.toString() + "'";
      else return "datum." + fieldname + " == " + elem.toString() + "";
    } else if (op === "neq") {
      if (typeof elem == "string") return "datum." + fieldname + " != '" + elem.toString() + "'";
      else return "datum." + fieldname + " != " + elem.toString() + "";
    } else if (op === "oneOf") {
      return "oneOf(datum." + fieldname + ", [" + elem.map(d => (typeof d == "string" ? "'" + d + "'" : d.toString())).join(", ") + "])";
    } else if (op === "leq") {
      return "datum." + fieldname + " <= " + elem.toString();
    } else if (op === "geq") {
      return "datum." + fieldname + " >= " + elem.toString();
    } else if (op === "lt") {
      return "datum." + fieldname + " < " + elem.toString();
    } else if (op === "gt") {
      return "datum." + fieldname + " > " + elem.toString();
    } else if (op === "range") {
      let min = (elem[0] > elem[1] ? elem[1] : elem[0]),
        max = (elem[0] < elem[1] ? elem[1] : elem[0]);
      return "(datum." + fieldname + " >= " + min.toString() + " && datum." + fieldname + " <= " + max.toString() + ")";
    } else if (op === "xrange") {
      let min = (elem[0] > elem[1] ? elem[1] : elem[0]),
        max = (elem[0] < elem[1] ? elem[1] : elem[0]);
      return "(datum." + fieldname + " > " + min.toString() + " && datum." + fieldname + " < " + max.toString() + ")";
    } else if (op === "startsWith") {
      return "startsWith(datum." + fieldname + ", '" + elem + "')";
    } else if (op === "endsWith") {
      return "endsWith(datum." + fieldname + ", '" + elem + "')";
    } else if (op === "startsEndsWith") {
      return "startsEndsWith(datum." + fieldname + ", '" + elem[0] + "', '" + elem[1] + "')";
    } else if (op === "includes") {
      return "includes(datum." + fieldname + ", '" + elem + "')";
    } else if (op === "pattern") {
      return "pattern(datum." + fieldname + ", /" + elem + "/g)";
    }
  }

  function makeSingleStatement(fieldname, object) {
    if (fieldname == null) fieldname = "value"
    if (object.constructor.name === "Object" && object.not !== undefined) {
      return "!" + makeSingleStatement(fieldname, object.not);
    } else {
      if (object.constructor.name === "Array") { // list of operators
        return "(" + object.map(item => {
          return makeSingleStatement(fieldname, item);
        }).join(" || ") + ")";
      } else if (object.constructor.name === "Object") { // list of operators
        return "(" + Object.keys(object).map(op => {
          return makeAtomicStatement(fieldname, object[op], op);
        }).join(" && ") + ")";
      } else { // a value
        return makeAtomicStatement(fieldname, object, "eq");
      }
    }
  }

  if (fObj.constructor.name === "Object") {
    // first-level: fields;
    return "(" + Object.keys(fObj).map(fieldname => {
      return makeSingleStatement(fieldname, fObj[fieldname]);
    }).join(" && ") + ")"
  } else if (fObj.constructor.name === "Array") {
    return "(" + fObj.map(d => toFilterString(d)).join(" || ") + ")"
  } else {
    return makeSingleStatement(null, fObj)
  }
}

export function makeFilterFunction(fObj, isAtomic) {
  let filterState;
  if (fObj?.constructor.name === "Object") filterState = toFilterString(fObj);
  else if (fObj?.constructor.name === "Array") filterState = toFilterString(fObj);
  else if (fObj?.constructor.name === "String") filterState = fObj;
  if (filterState) {
    return function (datum) {
      // pass a "single" datum row
      let preEval = `
    function startsWith(val, str) {
      return val.startsWith(str);
    }
    function endsWith(val, str) {
      return val.endsWith(str);
    }
    function startsEndsWith(val, start, end) {
      return val.startsWith(start) && val.endsWith(end);
    }
    function includes(val, str) {
      return val.includes(str);
    }
    function pattern(val, expr) {
      return val.match(expr).length > 0;
    }
    function oneOf(val, arr) {
      return arr.includes(val);
    }`;
      if (isAtomic) return eval(preEval + filterState.replace(/datum\.value/g, "(" + JSON.stringify(datum) + ")"))
      else return eval(preEval + filterState.replace(/datum/g, "(" + JSON.stringify(datum) + ")"))
    }
  } else {
    return () => true;
  }
}

export function aggregate_data(data, currField, method, groupby) {
  let new_field_name = (currField ? method + "_" + currField : method);
  groupby = Array.from(new Set(groupby));
  let grouped = {};
  data.forEach(datum => {
    let gkey = groupby.map(f => (datum[f] ? datum[f] : "<X>")).join("$$")
    if (grouped[gkey] === undefined) grouped[gkey] = [];
    grouped[gkey].push(datum[currField]);
  })
  let result = Object.keys(grouped).map(gkey => {
    let gval = gkey.split("$$");
    let agg_datum = {}
    groupby.forEach((k, i) => {
      if (gval !== "<X>") agg_datum[k] = gval[i];
      agg_datum[new_field_name] = d3[method](grouped[gkey]);
    });
    return agg_datum;
  });
  return result;
}

export function get_rc_field(d) {
  if (d.constructor.name === "Object" && d.field !== undefined) return d.field;
  else if (d.constructor.name === "Array") return d.map(f => get_rc_field(f));
  else if (d.constructor.name === "String") return d;
  else return undefined;
}

// anchoring helper
export function get8pos(x, y, w, h) {
  // top, mid, bottm X left, center, right combinations (i.e., 8pos)
  let padW = w * 0.05,
    padH = h * 0.05; // padding
  let pos = [
    [x + padW, y + padH], // top-left
    [x + w / 2, y + padH], // top-center
    [x + w - padW, y + padH], // top-right
    [x + w - padW, y + h / 2], // mid-right
    [x + w - padW, y + h - padH], // bottom-right
    [x + w / 2, y + h - padH], // bottom-center
    [x + padW, y + h - padH], // bottom-left
    [x + padW, y + h / 2], // mid-left
  ];
  return pos;
}

export function getClosest8Pos(a8, b8) {
  // get closes two position given 8pos
  let min = 9999999,
    minA,
    minB;
  for (let a of a8) {
    for (let b of b8) {
      let dist = Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2));
      if (dist < min) {
        min = dist;
        (minA = a), (minB = b);
      }
    }
  }
  return [minA, minB];
}

export function getLineDirection(xField, yField, dataset, datatype) {
  // rule-based
  if (datatype !== undefined) {
    if (datatype[xField].type === "nominal") {
      return "horizontal";
    } else if (datatype[yField].type === "nominal") {
      return "vertical";
    } else if (datatype[xField].type === "temporal") {
      return "horizontal";
    } else if (datatype[yField].type === "temporal") {
      return "vertical";
    }
  }
  // all the other cases (Q x Q)
  let xValues = datatype[xField]?.values;
  let yValues = datatype[yField]?.values;
  if (!xValues) {
    xValues = Array.from(new Set(dataset.map(d => d[xField]).filter(d => d !== undefined)))
  }
  if (!yValues) {
    yValues = Array.from(new Set(dataset.map(d => d[yField]).filter(d => d !== undefined)))
  }
  let xCount = {}, yCount = {};
  dataset.forEach(d => {
    if (xCount[d[xField]] === undefined) xCount[d[xField]] = 0;
    if (yCount[d[yField]] === undefined) yCount[d[yField]] = 0;
    xCount[d[xField]] += 1;
    yCount[d[yField]] += 1;
  })
  let xCnt = Object.keys(xCount).map(f => xCount[f]);
  let yCnt = Object.keys(yCount).map(f => yCount[f]);
  if (xCnt.every(d => d == 1) && yCnt.every(d => d == 1)) {
    let xValues2 = datatype[xField] == "temporal" ? xValues.map(d => Date.parse(d)) : xValues;
    let yValues2 = datatype[yField] == "temporal" ? yValues.map(d => Date.parse(d)) : yValues;
    xValues2.sort((a, b) => a - b);
    yValues2.sort((a, b) => a - b);
    if (xValues2.argAbsDiff().stdev() <= yValues2.argAbsDiff().stdev()) return "horizontal";
    else return "vertical";
  } else {
    if (xCnt.stdev() <= yCnt.stdev()) return "horizontal";
    else return "vertical";
  }
  // just in case;
  return "horizontal";
}

export function isTimeFormat(format) {
  let timeFormatRegex = /([\-\:\.\_\s]*\%[aAbBcdefgGHIjmMLpqQsSuUVwWxXyYzZ\%][\-\:\.\_\s]*)+/;
  return format.match(timeFormatRegex)?.[0] === format;
}

export function simpleNotUndefinedEq(a, b) {
  return a !== undefined && a == b;
}

export {
  frontoTitleGenerator as titleGenerator,
  frontoAnnotationGenerator as annotationGenerator,
  frontoEmphasisGenerator as emphasisGenerator,
  frontoBinCalc as get_bin_steps,
  frontoCompileSpace as compileSpace
};


// letter width setup
const letterWidth = {
  // scaled at font height 12px
  A: 7,
  a: 7,
  B: 8,
  b: 7,
  C: 8,
  c: 6,
  D: 9,
  d: 7,
  E: 7,
  e: 7,
  F: 7,
  f: 4,
  G: 9,
  g: 7,
  H: 9,
  h: 7,
  I: 3,
  i: 3,
  J: 5,
  j: 3,
  K: 8,
  k: 6,
  L: 7,
  l: 3,
  M: 11,
  m: 11,
  N: 9,
  n: 7,
  O: 9,
  o: 7,
  P: 8,
  p: 7,
  Q: 9,
  q: 7,
  R: 8,
  r: 4,
  S: 8,
  s: 6,
  T: 7,
  t: 4,
  U: 9,
  u: 7,
  V: 7,
  v: 6,
  W: 11,
  w: 9,
  X: 7,
  x: 6,
  Y: 7,
  y: 6,
  Z: 7,
  z: 5,
  ".": 2,
  ",": 2,
  ":": 2,
  ";": 2,
  others: 9,
  number: 7,
};
function getDefaultBoxWidth(text, height = 14) {
  if (text !== undefined) {
    let textRows = text.split(/\<br\/+\>/g);
    textRows = textRows.map(row => { return row.replace(/\<\/?[^\>]+\>/g, "") });
    let rowWidths = textRows.map((row) => {
      let textSplit = row.replace(/(\<.+\"\>)/g, "").split("");
      return (textSplit.map((d) => (letterWidth[d] ? letterWidth[d] : parseInt(d) || d === "0" ? letterWidth.number : letterWidth.others)).reduce((a, d) => a + d, 0) * height) / 11;
    });
    return d3.max(rowWidths);
  } else {
    return 0;
  }
}